import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Button, Stack, Snackbar } from '@mui/material';
import PinInput from 'react-pin-input';
import { useSignUpStore } from '../../../application/store/SignUpStore';
import DialogContainer from '@shared/ui/dialog/DialogContainer';
import DialogHeader from '@shared/ui/dialog/DialogHeader';
import { useAuthStore } from '../../../application/store/AuthStore';
import { useAuth } from '@shared/hooks/useAuth';
import { container } from '@infrastructure/container';
import { completeSignIn } from '../utils/completeSignIn';
import ResendCodeButton from '@features/sign-in/components/ResendCodeButton';

const pinLength = 6;

const SignInValidate = () => {
  const { email, userId } = useSignUpStore();
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { login, logout } = useAuth();

  const verifyCode = async (code: string) => {
    if (code.length !== pinLength) return;

    try {
      // Only logout if there's an existing session to avoid unnecessary session deletion
      const isLoggedIn = await container.authRepository.isUserLoggedIn();
      if (isLoggedIn.status === 'success' && isLoggedIn.data) {
        await logout();
      }
      
      const result = await container.authRepository.verifyCode(userId!, code);
      if (result.status === 'success') {
        useSignUpStore.getState().reset();

        const user = await container.authRepository.getCurrentUser();
        const token = await useAuthStore.getState().getJwt();

        if (user.status === 'error') {
          throw new Error('Failed to get user information');
        }

        if (!token) {
          throw new Error('Invalid token');
        }

        await completeSignIn({
          navigate,
          user: user.data,
          jwt: token,
          login,
        });
      } else {
        throw new Error('Invalid verification code');
      }
    } catch (error) {
      // Only logout if there's an existing session to avoid unnecessary session deletion
      const isLoggedIn = await container.authRepository.isUserLoggedIn();
      if (isLoggedIn.status === 'success' && isLoggedIn.data) {
        logout();
      }
      setError(error instanceof Error ? error.message : 'Invalid verification code');
    }
  };

  const sendCode = async () => {
    const result = await container.authRepository.sendCode(email!);

    if (result.status === 'success' && result.data?.userId) {
      useSignUpStore.getState().setUserId(result.data.userId);
      setError(null);
    } else {
      setError('Failed to send code');
    }
  };

  return (
    <Box sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      bgcolor: 'background.default',
      p: 0
    }}>
      <DialogContainer>
        <DialogHeader>
          <img height={16} src="/assets/images/ic_just_stock.png" alt="logo" />
        </DialogHeader>

        <Box sx={{
          p: 3,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          flex: 1
        }}>
          <Box>
            <Typography variant="body1" fontWeight="medium">Check your inbox</Typography>
            <Typography variant="body1" color="text.secondary">
              We sent a verification code to <strong>{email}</strong>
            </Typography>
          </Box>

          <PinInput
            length={pinLength}
            focus
            type="numeric"
            inputMode="numeric"
            inputStyle={{
              borderColor: error ? '#d32f2f' : '#e0e0e0',
              borderRadius: '4px',
              margin: '0 4px',
              width: '40px',
              height: '40px'
            }}
            onComplete={(value) => verifyCode(value)}
            onChange={() => setError(null)}
          />

          <ResendCodeButton onResend={sendCode} />

          <Box sx={{ flex: 1 }} />

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={3}
            sx={{
              justifyContent: 'space-between'
            }}
          >
            <Button
              variant="outlined"
              onClick={() => navigate(-1)}
              size="medium"
            >
              Back
            </Button>
            <Button
              variant="contained"
              onClick={() => verifyCode(code)}
              disabled={code.length !== pinLength}
              size="medium"
            >
              Continue
            </Button>
          </Stack>
        </Box>
      </DialogContainer>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        message={error}
      />
    </Box>
  );
};

export default SignInValidate;